import { useEffect, useRef } from 'react';
import { UnicodeEntity } from '@mews/enums';
import { InputValue, Size, Status, useComponentProps } from '@optimus-web/common';
import { useId } from '@optimus-web/helpers';
import { Asterisk } from '../common/FormLabel';
import { FormMessage } from '../common/FormMessage';
import { extractAriaProps } from '../utils/extractAriaProps';
import {
    CheckboxContainer,
    CheckboxElement,
    CheckMarkBackgroundElement,
    CheckMarkPathElement,
    CheckMarkSvgElement,
    InputLabel,
    LabelElement,
} from './style';

export interface CheckboxProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'onChange' | 'value' | 'name' | 'size' | 'onFocus' | 'onBlur'
    > {
    error?: React.ReactNode;
    'data-test-id'?: string;
    indeterminate?: boolean;
    name?: string;
    onChange?: (event: React.SyntheticEvent<HTMLElement>, checked: boolean) => void;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
    size?: Exclude<Size, 'extraLarge'>;
    value?: InputValue;
    stopClickPropagation?: boolean;
    as?: React.ElementType;
    ariaLabel?: string;
}

export const Checkbox = ({
    children,
    disabled,
    id,
    name,
    value,
    tabIndex,
    checked,
    required,
    className,
    error,
    size: customSize,
    indeterminate,
    onChange,
    onFocus,
    onBlur,
    'data-test-id': dataTestId,
    stopClickPropagation,
    as,
    ariaLabel,
    ...other
}: CheckboxProps) => {
    const { size } = useComponentProps('Checkbox', { size: customSize });
    const checkboxRef = useRef<HTMLInputElement>(null);
    const generatedId = useId('checkbox');
    const finalId = id || generatedId;

    const [ariaProps, otherProps] = extractAriaProps(other);

    useEffect(() => {
        if (checkboxRef.current !== null) {
            checkboxRef.current.indeterminate = !!indeterminate;
        }
    }, [indeterminate]);

    const handleChange = (event: React.SyntheticEvent<HTMLElement>) => {
        onChange?.(event, !checked);
    };

    const handleCheckboxClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.preventDefault();
        if (stopClickPropagation) {
            event.stopPropagation();
        }
        (event.target as HTMLInputElement).checked = !checked;
        handleChange(event);
    };

    return (
        <CheckboxContainer as={as} className={className} data-test-id={dataTestId}>
            <InputLabel disabled={disabled} withoutLabel={!children}>
                <CheckboxElement
                    ref={checkboxRef}
                    type="checkbox"
                    name={name}
                    value={value}
                    onChange={handleChange}
                    checked={checked}
                    id={finalId}
                    disabled={disabled}
                    tabIndex={-1}
                    required={required}
                    aria-hidden
                    $size={size}
                    hasChildren={!!children}
                    {...otherProps}
                />
                <CheckMarkBackgroundElement
                    checked={checked}
                    disabled={disabled}
                    size={size}
                    indeterminate={indeterminate}
                    withoutLabel={!children}
                    error={!!error}
                    onClick={handleCheckboxClick}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    type="button"
                    role="checkbox"
                    aria-disabled={disabled}
                    aria-invalid={!!error}
                    aria-required={required}
                    aria-errormessage={error ? `${name}-error` : undefined}
                    aria-labelledby={children ? `${finalId}-label` : undefined}
                    aria-label={!children ? ariaLabel : undefined}
                    aria-checked={indeterminate ? 'mixed' : checked}
                    {...ariaProps}
                >
                    <CheckMarkSvgElement
                        checked={checked}
                        indeterminate={indeterminate}
                        width={size === 'large' ? '15' : '10'}
                        height={size === 'large' ? '12' : '8'}
                        viewBox="0 0 10 8"
                        fill="none"
                        role="presentation"
                    >
                        <CheckMarkPathElement
                            checked={checked}
                            disabled={disabled}
                            d="M8.23196 0.361083C8.58565 -0.0630916 9.21623 -0.120233 9.64041 0.233454C10.032 0.559934 10.1108 1.12235 9.84238 1.54057L9.76804 1.6419L4.7685 7.63781C4.41883 8.05716 3.80491 8.11368 3.38616 7.78665L3.2932 7.70436L0.292741 4.70261C-0.0976991 4.312 -0.097563 3.67884 0.293045 3.2884C0.653607 2.92799 1.22084 2.90038 1.61307 3.20549L1.70726 3.2887L3.933 5.51508L8.23196 0.361083Z"
                        />
                    </CheckMarkSvgElement>
                </CheckMarkBackgroundElement>
                {children ? (
                    <LabelElement id={`${finalId}-label`} size={size} required={required} aria-hidden>
                        {children}
                        {required ? <Asterisk aria-hidden>{UnicodeEntity.Asterisk}</Asterisk> : null}
                    </LabelElement>
                ) : null}
            </InputLabel>
            {error ? (
                <FormMessage status={Status.Error} id={`${name}-error`}>
                    {error}
                </FormMessage>
            ) : null}
        </CheckboxContainer>
    );
};
